import { GUARD_STRUCTURE, METHOD_CONSTANT_LIMITS, MethodConstantKey } from './cohort-guard-defaults';
import { GUARD_OF_PARAM, GUARD_PARAMS, GuardId, GuardParam, GuardParamValue, ResolvedGuards, isValidGuardParam } from './cohort-guards';

export const GUARD_LABEL: Record<GuardId, string> = {
  legacy: 'Bundle early cohorts',
  credible: 'Cohorts the forecast learns from',
  anchors: 'Learn from actual results only',
  trend: 'Largest yearly change in retention',
  tail: 'Long-term fade',
  cap: 'Safety ceiling on repeat rate',
  qa: 'Red cell warnings',
  retention: 'Retention change',
  own: 'Own history for mature cohorts'
};

export const GUARD_FLAG_RULE: Record<'R1' | 'R2' | 'R3', string> = {
  R1: 'Above the safety ceiling for its age group',
  R2: 'Far above the normal rate for its age',
  R3: 'Held down by the safety ceiling'
};

type ParamKind = 'month' | 'pct' | 'signed' | 'onoff' | 'int' | 'orders' | 'multiple' | 'list' | 'limits' | 'caps';

export const PARAM_GUIDE: Record<GuardParam, { label: string; kind: ParamKind }> = {
  legacyCutoff: { label: 'Bundle cohorts that started before', kind: 'month' },
  legacyRetention: { label: 'Share of bundle orders kept each month', kind: 'pct' },
  legacyCeiling: { label: 'Highest share of bundle orders kept each month', kind: 'pct' },
  minSize: { label: 'Minimum first month orders to learn from a cohort', kind: 'orders' },
  k: { label: 'Number of recent cohorts averaged', kind: 'int' },
  trendWindow: { label: 'Months compared to measure the trend', kind: 'int' },
  trendBands: { label: 'Age groups', kind: 'list' },
  trendLimits: { label: 'Largest yearly change in retention', kind: 'limits' },
  trendApplications: { label: 'Years the trend is carried forward', kind: 'int' },
  tailWindow: { label: 'Mature ages used to measure the fade', kind: 'int' },
  rateCap: { label: 'Safety ceiling on repeat rate', kind: 'caps' },
  qaMultiple: { label: 'Warning level for red cells', kind: 'multiple' },
  retentionChange: { label: 'Change in retention', kind: 'signed' },
  retentionChangeStart: { label: 'Retention change starts', kind: 'month' },
  ownHistoryOn: { label: 'Mature cohorts use their own history', kind: 'onoff' },
  ownHistoryMinMonths: { label: 'Actual months needed to use own history', kind: 'int' },
  bundleRetentionWindow: { label: 'Recent months used for the bundle yearly share', kind: 'int' },
  ceilingHeadroom: { label: 'Headroom above the best real rate', kind: 'caps' }
};

export const CLIENT_PARAMS: GuardParam[] = ['legacyCutoff', 'trendLimits', 'trendApplications', 'legacyRetention', 'minSize'];

export const CONSTANT_GUIDE: Record<MethodConstantKey, { label: string; pct?: boolean }> = {
  evidenceTarget: { label: 'Minimum repeat orders behind each average' },
  matureAgeFrom: { label: 'Ages used to measure the typical repeat rate: from' },
  matureAgeTo: { label: 'Ages used to measure the typical repeat rate: to' },
  matureHistoryMonths: { label: 'Recent cohorts used for the typical repeat rate (months)' },
  steadyRun: { label: 'Steady months needed to end the bundle' },
  tailStartAge: { label: 'Earliest age used to measure the fade' },
  qaPercentile: { label: 'Share of real results that must pass the warning level', pct: true },
  autoWindow: { label: 'Recent months Auto tests each forecast option on' }
};

const AGE_GROUP_NAMES = ['First repeat order', 'Early months', 'Rest of the first half year', 'Second half of the first year', 'Second year', 'Long-standing customers'];
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const isNum = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v);
const trim = (v: number, dp: number) => String(Math.round(v * Math.pow(10, dp)) / Math.pow(10, dp));

export function ageGroupsOf(bands: number[]): Array<{ name: string; months: string }> {
  const standard = bands.length === GUARD_STRUCTURE.trendBands.length && bands.every((b, i) => b === GUARD_STRUCTURE.trendBands[i]);
  return bands.map((start, i) => {
    const end = i + 1 < bands.length ? bands[i + 1] - 1 : null;
    const months = end === null ? start + ' and over' : end === start ? String(start) : start + ' to ' + end;
    return { name: standard ? AGE_GROUP_NAMES[i] : 'Months ' + months, months };
  });
}

export function formatMonth(ym: number): string {
  return MONTHS[(ym % 100) - 1] + ' ' + Math.floor(ym / 100);
}

export function formatGuardValue(param: GuardParam, v: GuardParamValue | undefined): string {
  if (v === null || v === undefined) return '';
  const kind = PARAM_GUIDE[param].kind;
  if (Array.isArray(v)) {
    if (kind === 'limits') {
      const out: string[] = [];
      for (let i = 0; i + 1 < v.length; i += 2) out.push('fall ' + (limitPct(v[i], 'fall') || '–') + '% / rise ' + (limitPct(v[i + 1], 'rise') || '–') + '%');
      return out.join(', ');
    }
    if (kind === 'caps') return v.map(x => (isNum(x) ? trim(x * 100, 1) + '%' : '–')).join(', ');
    return v.join(', ');
  }
  if (kind === 'month') return formatMonth(v);
  if (kind === 'onoff') return v === 1 ? 'On' : 'Off';
  if (kind === 'signed') return (v > 0 ? '+' : '') + trim(v * 100, 2) + '%';
  if (kind === 'pct' || kind === 'caps') return trim(v * 100, 2) + '%';
  if (kind === 'multiple') return trim(v, 2) + '×';
  if (kind === 'orders') return trim(v, 0);
  return trim(v, 2);
}

export function limitPct(v: number | null | undefined, side: 'fall' | 'rise'): string {
  if (!isNum(v)) return '';
  return trim(Math.abs(side === 'fall' ? 1 - v : v - 1) * 100, 1);
}

export function parseLimitPct(text: string): number | undefined {
  const s = String(text || '').trim().replace(/%$/, '').trim();
  if (!s) return undefined;
  const n = Number(s);
  return isNum(n) && n >= 0 && n < 100 ? n : undefined;
}

export function parseMonth(text: string): number | undefined {
  const s = String(text || '').trim();
  const named = /^([a-z]{3})[a-z]*[\s\-/]+(\d{2}|\d{4})$/i.exec(s);
  if (named) {
    const m = MONTHS.findIndex(x => x.toLowerCase() === named[1].toLowerCase());
    const y = named[2].length === 2 ? 2000 + Number(named[2]) : Number(named[2]);
    return m >= 0 ? y * 100 + m + 1 : undefined;
  }
  const iso = /^(\d{4})[-/ ]?(\d{1,2})$/.exec(s);
  if (iso) return Number(iso[1]) * 100 + Number(iso[2]);
  const us = /^(\d{1,2})[-/ ](\d{4})$/.exec(s);
  return us ? Number(us[2]) * 100 + Number(us[1]) : undefined;
}

export function parseGuardValue(param: GuardParam, text: string): GuardParamValue | undefined {
  const s = String(text || '').trim().replace(/[×x%\s]+$/i, '').trim();
  if (!s) return undefined;
  const kind = PARAM_GUIDE[param].kind;
  let v: GuardParamValue | undefined;
  if (kind === 'month') {
    v = parseMonth(String(text || ''));
  } else if (kind === 'onoff') {
    const t = s.toLowerCase();
    v = t === 'on' || t === '1' ? 1 : t === 'off' || t === '0' ? 0 : undefined;
  } else if (kind === 'list') {
    const xs = s.split(/[,;\s]+/).filter(Boolean).map(Number);
    v = xs.every(isNum) ? xs : undefined;
  } else if (kind === 'limits') {
    const xs: number[] = [];
    const ok = s.split(/[,;]+/).map(p => p.trim()).filter(Boolean).every(p => {
      const m = /^(\d*\.?\d+)\s*(?:-|to)\s*(\d*\.?\d+)$/.exec(p);
      if (!m) return false;
      xs.push(Number(m[1]), Number(m[2]));
      return true;
    });
    v = ok ? xs : undefined;
  } else if (kind === 'caps') {
    const xs = s.split(/[,;\s]+/).filter(Boolean).map(p => Number(p.replace(/%$/, '')) / 100);
    v = xs.every(isNum) ? (xs.length === 1 ? xs[0] : xs) : undefined;
  } else {
    const n = Number(s);
    if (!isNum(n)) return undefined;
    v = kind === 'pct' || kind === 'signed' ? n / 100 : n;
  }
  return v !== undefined && isValidGuardParam(param, v) ? v : undefined;
}

export type GuardState = 'on' | 'off' | 'needs';

export const PARAMS_OF_GUARD: Record<GuardId, GuardParam[]> = GUARD_PARAMS.reduce((acc, p) => {
  acc[GUARD_OF_PARAM[p]].push(p);
  return acc;
}, { legacy: [], credible: [], anchors: [], trend: [], tail: [], cap: [], qa: [], retention: [], own: [] } as Record<GuardId, GuardParam[]>);

export function guardStateOf(resolved: ResolvedGuards, guard: GuardId): GuardState {
  if (resolved.off[guard]) return 'off';
  if (resolved.needs.some(p => GUARD_OF_PARAM[p] === guard) || (guard === 'tail' && resolved.fadeNeeds)) return 'needs';
  return resolved.on[guard] ? 'on' : 'off';
}

export function fadeSourceText(resolved: ResolvedGuards): string {
  return resolved.fadeFrom === 'curve' ? ' · from mature cohorts' : resolved.fadeFrom === 'bundle' ? ' · from the bundle' : '';
}

export function guardValueText(resolved: ResolvedGuards, guard: GuardId): string {
  const show = (p: GuardParam) => formatGuardValue(p, resolved.params[p] ? resolved.params[p].value : null);
  switch (guard) {
    case 'legacy': return [show('legacyCutoff') ? 'before ' + show('legacyCutoff') : '', show('legacyRetention') ? show('legacyRetention') + ' kept a month' : ''].filter(Boolean).join(' · ');
    case 'credible': return show('minSize') ? show('minSize') + ' first month orders' : '';
    case 'trend': return resolved.trendUsed ? 'up to ' + maxYearlyChange(resolved.limits) + '% a year' : 'not used';
    case 'tail':
      if (isNum(resolved.fade)) return trim(resolved.fade * 100, 1) + '% kept each month' + fadeSourceText(resolved);
      return isNum(resolved.tailRate) ? trim(resolved.tailRate * 100, 2) + '%' : '';
    case 'cap': return show('rateCap');
    case 'qa': return show('qaMultiple');
    case 'retention': return resolved.retention.value ? show('retentionChange') + (isNum(resolved.retention.start) ? ' from ' + formatMonth(resolved.retention.start) : '') : 'none';
    case 'own': return resolved.ownOn ? 'after ' + resolved.ownMinMonths + ' actual months' : 'not used';
    default: return '';
  }
}

export function maxYearlyChange(limits: number[]): string {
  let top = 0;
  limits.forEach((v, i) => { top = Math.max(top, Math.abs(i % 2 === 0 ? 1 - v : v - 1)); });
  return trim(top * 100, 1);
}

export function formatConstant(key: MethodConstantKey, v: number): string {
  return CONSTANT_GUIDE[key].pct ? v + '%' : String(v);
}

export function parseGuardConstant(key: MethodConstantKey, text: string): number | undefined {
  const s = String(text || '').trim().replace(/%$/, '').trim();
  if (!s) return undefined;
  const n = Number(s);
  const [lo, hi] = METHOD_CONSTANT_LIMITS[key];
  return Number.isFinite(n) && (CONSTANT_GUIDE[key].pct || Number.isInteger(n)) && n >= lo && n <= hi ? n : undefined;
}
